/**
 * Kullanıcı Takibi — /kullanici-takip. Sadece efe@heyglowy.com.
 * Panel kullanıcılarının giriş geçmişi ve sayfa hareketleri, marka marka.
 * Veri /api/kullanici-takip'ten gelir (backend e-posta kilitli).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Search, X, RefreshCw, LogIn, RotateCcw, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';

const API = 'https://api.glowytics.com/api/kullanici-takip';

const T = {
  kart: '#FFFFFF', cizgi: '#E5E7EB', cizgi2: '#F3F4F6', yuzey: '#F8FAFF',
  metin: '#111827', metin2: '#374151', metin3: '#6B7280', mavi: '#436AEB',
  kirmizi: '#DC2626', turuncu: '#D97706', yesil: '#059669',
};

const IC_ALAN = /@(heyglowy|glowytics)\.com$/i;
const DONEMLER = [7, 30, 90, 365];

const gunOnce = (iso) => (iso ? Math.floor((Date.now() - new Date(iso)) / 86400000) : null);
const tarih = (iso) => (iso ? new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Istanbul' }) : '—');
const tarihSaat = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }) : '—');
const dakika = (d) => (d >= 60 ? `${Math.floor(d / 60)} sa ${Math.round(d % 60)} dk` : `${Math.round(d)} dk`);

const tarayici = (ua = '') => {
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : 'Diğer';
  const br = /Edg/.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Diğer';
  return `${br} · ${os}`;
};

async function cagir(yol = '') {
  const res = await apiFetch(`${API}${yol}`, { headers: { 'Content-Type': 'application/json' } });
  const govde = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(govde.error || `HTTP ${res.status}`);
  return govde;
}

function Cip({ aktif, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: 'pointer',
      border: `1px solid ${aktif ? T.mavi : T.cizgi}`, background: aktif ? T.mavi : T.kart, color: aktif ? '#fff' : T.metin2,
    }}>{children}</button>
  );
}

function SonGorulme({ iso }) {
  const g = gunOnce(iso);
  if (g === null) return <span style={{ color: T.metin3 }}>Hiç</span>;
  const renk = g > 60 ? T.kirmizi : g > 14 ? T.turuncu : T.yesil;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: renk }} />
      {g === 0 ? 'Bugün' : `${g} gün önce`}
    </span>
  );
}

function Kutu({ baslik, children, sag }) {
  return (
    <div style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.metin3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{baslik}</div>
        {sag && <div style={{ marginLeft: 'auto', fontSize: 12, color: T.metin3 }}>{sag}</div>}
      </div>
      {children}
    </div>
  );
}

const th = { textAlign: 'left', fontSize: 12, fontWeight: 600, color: T.metin3, padding: '8px 10px', borderBottom: `1px solid ${T.cizgi}`, whiteSpace: 'nowrap' };
const td = { fontSize: 13, color: T.metin2, padding: '9px 10px', borderBottom: `1px solid ${T.cizgi2}`, verticalAlign: 'top' };

function GunlukGrafik({ gunluk, gun }) {
  // Boş günleri de çiz ki boşluk görünsün.
  const harita = Object.fromEntries(gunluk.map((g) => [g.gun, g]));
  const gunler = [];
  for (let i = gun - 1; i >= 0; i -= 1) {
    const d = new Date(Date.now() - i * 86400000);
    const k = d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
    gunler.push({ k, sayfa: harita[k]?.sayfa || 0, dk: harita[k]?.aktifDakika || 0 });
  }
  const enCok = Math.max(1, ...gunler.map((g) => g.sayfa));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 80 }}>
        {gunler.map((g) => (
          <div key={g.k} title={`${g.k} · ${g.sayfa} sayfa · ${dakika(g.dk)}`} style={{
            flex: 1, minWidth: 1, height: g.sayfa ? `${Math.max(6, (g.sayfa / enCok) * 100)}%` : 2,
            background: g.sayfa ? T.mavi : T.cizgi2, borderRadius: 2,
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', fontSize: 11, color: T.metin3, marginTop: 6 }}>
        <span>{tarih(gunler[0]?.k)}</span><span style={{ marginLeft: 'auto' }}>Bugün</span>
      </div>
    </div>
  );
}

export function Detay({ id, takipBaslangici = '2026-01-08T10:56:01Z', onKapat }) {
  const [gun, setGun] = useState(90);
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);

  useEffect(() => {
    setVeri(null); setHata(null);
    cagir(`/kullanici/${id}?gun=${gun}`).then(setVeri).catch((e) => setHata(e.message));
  }, [id, gun]);

  const ozet = useMemo(() => {
    if (!veri) return null;
    return {
      giris: veri.oturumlar.filter((o) => o.giris).length,
      ziyaret: veri.oturumlar.length,
      sayfa: veri.gunluk.reduce((t, g) => t + g.sayfa, 0),
      dk: veri.gunluk.reduce((t, g) => t + g.aktifDakika, 0),
      aktifGun: veri.gunluk.length,
    };
  }, [veri]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.35)', display: 'flex', justifyContent: 'flex-end' }} onClick={onKapat}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(860px, 100%)', height: '100%', overflowY: 'auto', background: '#F9FAFB', padding: '20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: T.metin, margin: 0, wordBreak: 'break-all' }}>{veri?.kullanici.email || '…'}</h2>
            {veri && (
              <div style={{ fontSize: 13, color: T.metin3, marginTop: 4 }}>
                {veri.kullanici.name || 'İsimsiz'} · kayıt {tarih(veri.kullanici.kayit)} · {veri.kullanici.dogrulandi ? 'e-posta doğrulanmış' : 'e-posta doğrulanmamış'}
              </div>
            )}
          </div>
          <button type="button" onClick={onKapat} aria-label="Kapat" style={{ marginLeft: 'auto', border: `1px solid ${T.cizgi}`, background: T.kart, borderRadius: 8, padding: 6, cursor: 'pointer' }}>
            <X size={18} color={T.metin2} />
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {[30, 90, 365].map((g) => <Cip key={g} aktif={gun === g} onClick={() => setGun(g)}>Son {g} gün</Cip>)}
        </div>

        {hata && <div style={{ padding: 12, borderRadius: 8, background: '#FEF2F2', color: '#B91C1C', fontSize: 14 }}>{hata}</div>}
        {!veri && !hata && <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.metin3, fontSize: 14 }}><RefreshCw size={14} /> Yükleniyor…</div>}

        {veri && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
              {[
                ['Şifreli giriş', ozet.giris, 'tüm zamanlar'],
                ['Ziyaret', ozet.ziyaret, 'giriş + oturum yenileme'],
                ['Sayfa görüntüleme', ozet.sayfa, `son ${gun} gün`],
                ['Aktif süre', dakika(ozet.dk), `son ${gun} gün, sekme açıkken`],
                ['Aktif gün', ozet.aktifGun, `son ${gun} gün`],
              ].map(([ad, deger, alt]) => (
                <div key={ad} style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: T.metin, lineHeight: 1 }}>{deger}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.metin, marginTop: 8 }}>{ad}</div>
                  <div style={{ fontSize: 11, color: T.metin3, marginTop: 2 }}>{alt}</div>
                </div>
              ))}
            </div>

            <Kutu baslik="Günlük sayfa görüntüleme">
              <GunlukGrafik gunluk={veri.gunluk} gun={gun} />
            </Kutu>

            <Kutu baslik="Marka erişimi">
              {veri.markalar.length === 0 && <div style={{ fontSize: 13, color: T.metin3 }}>Erişim yok.</div>}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Marka</th><th style={th}>Rol</th><th style={th}>İzinler</th><th style={th}>Verildi</th><th style={th}>Veren</th></tr></thead>
                  <tbody>
                    {veri.markalar.map((m) => (
                      <tr key={m.id}>
                        <td style={{ ...td, fontWeight: 600, color: T.metin }}>{m.ad}</td>
                        <td style={td}>{m.rol}</td>
                        <td style={td}>{Object.entries(m.izinler || {}).filter(([, v]) => v).map(([k]) => k).join(', ') || '—'}</td>
                        <td style={td}>{tarih(m.verildi)}</td>
                        <td style={td}>{m.veren || 'sistem'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Kutu>

            <Kutu baslik="Giriş ve ziyaretler" sag="Ziyaret = mevcut oturumla panele geri dönüş">
              {veri.oturumlar.length === 0 && <div style={{ fontSize: 13, color: T.metin3 }}>Kayıt yok.</div>}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Zaman</th><th style={th}>Tür</th><th style={th}>Tarayıcı</th></tr></thead>
                  <tbody>
                    {veri.oturumlar.map((o) => (
                      <tr key={`${o.aile}-${o.zaman}`}>
                        <td style={td}>{tarihSaat(o.zaman)}</td>
                        <td style={td}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: o.giris ? T.mavi : T.metin2, fontWeight: o.giris ? 600 : 400 }}>
                            {o.giris ? <LogIn size={14} /> : <RotateCcw size={14} />}{o.giris ? 'Şifreli giriş' : 'Ziyaret'}
                          </span>
                        </td>
                        <td style={td}>{tarayici(o.ua)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {veri.girisDenemeleri.some((g) => !g.basarili) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, padding: '10px 12px', background: '#FFFBEB', borderRadius: 8, fontSize: 13, color: '#92400E' }}>
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    {veri.girisDenemeleri.filter((g) => !g.basarili).length} başarısız giriş denemesi; son {tarihSaat(veri.girisDenemeleri.find((g) => !g.basarili).zaman)}
                    {' '}({veri.girisDenemeleri.find((g) => !g.basarili).neden || 'neden yok'})
                  </span>
                </div>
              )}
            </Kutu>

            <Kutu baslik="Sayfalar" sag={`son ${gun} gün`}>
              {veri.sayfalar.length === 0 && <div style={{ fontSize: 13, color: T.metin3 }}>Bu dönemde sayfa hareketi yok.</div>}
              {veri.sayfalar.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Sayfa</th><th style={{ ...th, textAlign: 'right' }}>Görüntüleme</th><th style={{ ...th, textAlign: 'right' }}>Aktif süre</th><th style={th}>Son</th></tr></thead>
                    <tbody>
                      {veri.sayfalar.map((s) => (
                        <tr key={s.yol}>
                          <td style={{ ...td, color: T.metin, wordBreak: 'break-all' }}>{s.yol}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{s.sayfa}</td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{dakika(s.aktifDakika)}</td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{tarihSaat(s.son)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Kutu>

            <Kutu baslik="Son hareketler" sag="son 300, kalp atışları hariç">
              {veri.zaman.length === 0 && <div style={{ fontSize: 13, color: T.metin3 }}>Kayıt yok.</div>}
              {veri.zaman.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Zaman</th><th style={th}>Sayfa</th><th style={th}>Marka</th></tr></thead>
                    <tbody>
                      {veri.zaman.map((z, i) => (
                        <tr key={i}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{tarihSaat(z.zaman)}</td>
                          <td style={{ ...td, color: T.metin, wordBreak: 'break-all' }}>{z.yol}{z.detay ? <span style={{ color: T.metin3 }}> · {z.detay}</span> : null}</td>
                          <td style={td}>{z.marka || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Kutu>

            <div style={{ fontSize: 12, color: T.metin3, lineHeight: 1.6 }}>
              Sayfa hareketleri {tarih(takipBaslangici)} tarihinden beri kaydediliyor; öncesi için yalnız giriş ve ziyaret tarihleri var.
              Aktif süre, sekme görünürken 30 saniyede bir gelen sinyallerden hesaplanır.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function KullaniciTakip() {
  const [gun, setGun] = useState(30);
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);
  const [marka, setMarka] = useState('');
  const [ara, setAra] = useState('');
  const [icGizle, setIcGizle] = useState(true);
  const [filtre, setFiltre] = useState('tumu'); // tumu | aktif | uyuyan | hic
  const [secili, setSecili] = useState(null);

  useEffect(() => {
    setVeri(null); setHata(null);
    cagir(`?gun=${gun}`).then(setVeri).catch((e) => setHata(e.message));
  }, [gun]);

  const liste = useMemo(() => {
    let l = veri?.kullanicilar || [];
    if (icGizle) l = l.filter((k) => !IC_ALAN.test(k.email));
    if (marka) l = l.filter((k) => k.markalar.some((m) => m.id === marka));
    if (ara.trim()) {
      const q = ara.trim().toLocaleLowerCase('tr-TR');
      l = l.filter((k) => k.email.toLowerCase().includes(q) || (k.name || '').toLocaleLowerCase('tr-TR').includes(q)
        || k.markalar.some((m) => m.ad.toLocaleLowerCase('tr-TR').includes(q)));
    }
    if (filtre === 'aktif') l = l.filter((k) => k.sayfa > 0);
    if (filtre === 'uyuyan') l = l.filter((k) => k.son_gorulme && gunOnce(k.son_gorulme) > 30);
    if (filtre === 'hic') l = l.filter((k) => !k.son_gorulme);
    return l;
  }, [veri, icGizle, marka, ara, filtre]);

  const markaSecenekleri = useMemo(() => [...(veri?.markalar || [])].sort((a, b) => a.ad.localeCompare(b.ad, 'tr')), [veri]);

  return (
    <div style={{ padding: '24px 16px', maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Activity size={20} color={T.mavi} />
        </div>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.metin, margin: 0 }}>Kullanıcı Takibi</h1>
          <div style={{ fontSize: 13, color: T.metin3 }}>Panel kullanıcılarının giriş ve sayfa hareketleri</div>
        </div>
      </div>

      {hata && <div style={{ margin: '16px 0', padding: 12, borderRadius: 8, background: '#FEF2F2', color: '#B91C1C', fontSize: 14 }}>{hata}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '20px 0 12px' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <Search size={15} color={T.metin3} style={{ position: 'absolute', left: 10, top: 9 }} />
          <input value={ara} onChange={(e) => setAra(e.target.value)} placeholder="E-posta, ad veya marka ara"
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 32px', fontSize: 13, borderRadius: 8, border: `1px solid ${T.cizgi}` }} />
        </div>
        <select value={marka} onChange={(e) => setMarka(e.target.value)} aria-label="Marka"
          style={{ fontSize: 13, padding: '7px 8px', borderRadius: 8, border: `1px solid ${T.cizgi}`, color: T.metin2, background: T.kart, maxWidth: 240 }}>
          <option value="">Tüm markalar</option>
          {markaSecenekleri.map((m) => <option key={m.id} value={m.id}>{m.ad} ({m.kullanici})</option>)}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.metin2, cursor: 'pointer' }}>
          <input type="checkbox" checked={icGizle} onChange={(e) => setIcGizle(e.target.checked)} /> İç hesapları gizle
        </label>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <Cip aktif={filtre === 'tumu'} onClick={() => setFiltre('tumu')}>Tümü</Cip>
        <Cip aktif={filtre === 'aktif'} onClick={() => setFiltre('aktif')}>Dönemde aktif</Cip>
        <Cip aktif={filtre === 'uyuyan'} onClick={() => setFiltre('uyuyan')}>30+ gündür girmeyen</Cip>
        <Cip aktif={filtre === 'hic'} onClick={() => setFiltre('hic')}>Hiç girmemiş</Cip>
        <span style={{ width: 1, background: T.cizgi, margin: '0 4px' }} />
        {DONEMLER.map((g) => <Cip key={g} aktif={gun === g} onClick={() => setGun(g)}>Son {g} gün</Cip>)}
      </div>

      {!veri && !hata && <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.metin3, fontSize: 14 }}><RefreshCw size={14} /> Yükleniyor…</div>}

      {veri && (
        <div style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
            <thead>
              <tr>
                <th style={th}>Kullanıcı</th>
                <th style={th}>Markalar</th>
                <th style={th}>Son görülme</th>
                <th style={{ ...th, textAlign: 'right' }}>Giriş / ziyaret</th>
                <th style={{ ...th, textAlign: 'right' }}>Sayfa ({gun} g)</th>
                <th style={{ ...th, textAlign: 'right' }}>Aktif süre ({gun} g)</th>
                <th style={{ ...th, textAlign: 'right' }}>Aktif gün</th>
              </tr>
            </thead>
            <tbody>
              {liste.map((k) => (
                <tr key={k.id} onClick={() => setSecili(k.id)} style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.yuzey; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: T.metin, wordBreak: 'break-all' }}>{k.email}</div>
                    <div style={{ fontSize: 12, color: T.metin3 }}>{k.name || '—'} · kayıt {tarih(k.kayit)}</div>
                  </td>
                  <td style={{ ...td, maxWidth: 260 }}>
                    {k.markalar.length === 0 ? <span style={{ color: T.metin3 }}>—</span>
                      : k.markalar.length > 3 ? `${k.markalar.slice(0, 2).map((m) => m.ad).join(', ')} +${k.markalar.length - 2}`
                        : k.markalar.map((m) => m.ad).join(', ')}
                  </td>
                  <td style={td}>
                    <SonGorulme iso={k.son_gorulme} />
                    <div style={{ fontSize: 12, color: T.metin3, marginTop: 2 }}>{k.son_gorulme ? tarih(k.son_gorulme) : ''}</div>
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {k.giris} / {k.ziyaret}
                    {k.basarisiz > 0 && <div style={{ fontSize: 12, color: T.kirmizi }}>{k.basarisiz} başarısız</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>{k.sayfa || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{k.aktifDakika ? dakika(k.aktifDakika) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{k.aktif_gun || '—'}</td>
                </tr>
              ))}
              {liste.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: T.metin3, padding: 24 }}>Bu filtrede kullanıcı yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {veri && (
        <div style={{ fontSize: 12, color: T.metin3, marginTop: 12, lineHeight: 1.6 }}>
          {liste.length} kullanıcı gösteriliyor. Sayfa hareketleri {tarih(veri.takipBaslangici)} tarihinden beri kaydediliyor.
          Giriş = şifreyle yeni oturum, ziyaret = giriş + mevcut oturumla panele geri dönüş.
        </div>
      )}

      {secili && <Detay id={secili} takipBaslangici={veri?.takipBaslangici} onKapat={() => setSecili(null)} />}
    </div>
  );
}
